// A track on a phone, after SPhoneTrack: back to the space, notifications; the space over the track's name with its
// lead, open count and health; the assistant on this track; then Activities (add a task, the rows), Context (what
// the space gives it, then its own), or Files.
import { AddButton } from '../ui/AddButton';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { can, type SessionUser, type TrackDetail } from '@dpw/shared';
import { api } from '../api';
import { useChange, useTrackActivities, useTrackFiles } from '../lib/queries';
import { HEALTH, ErrorNote } from '../ui/bits';
import { ContextPanel, ParentContext } from '../ui/ContextPanel';
import { SkelList } from '../ui/motion';
import { useToast } from '../ui/toast';
import { BellButton, BriefCard, P, Seg, Title, TopBar } from './kit';
import { ActivityRow } from './rows';
import { AddFileSheet, FileRows } from './files';
import { useRemember } from './recent';

type Tab = 'activities' | 'context' | 'files';

export function PhoneTrack({ t, me }: { t: TrackDetail; me: SessionUser }) {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'context' ? 'context' : params.get('tab') === 'files' ? 'files' : 'activities';
  const setTab = (x: Tab) => setParams(x === 'activities' ? {} : { tab: x }, { replace: true });
  const acts = useTrackActivities(t.id, false);
  const files = useTrackFiles(t.id);
  const [filter, setFilter] = useState<'all' | 'track' | 'activities'>('all');
  const [title, setTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const toast = useToast();
  const quick = useChange((x: string) => api.tracks.quickAdd(t.id, x));
  const upload = useChange((f: File) => api.tracks.upload(t.id, f, f.name));
  useRemember({ to: `/tracks/${t.id}`, title: t.name, sub: `Track · ${t.spaceName} · ${t.open} open`, kind: 'track' });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = title.trim();
    if (v) quick.mutate(v, { onSuccess: a => { setTitle(''); toast(`Added "${a.title}" as a task for you`); } });
  };
  const shown = (files.data ?? []).filter(f => filter === 'all' || (filter === 'track' ? !f.activityId : !!f.activityId));
  return (
    <div className="ph-page">
      <TopBar back={{ to: `/spaces/${t.spaceId}`, label: t.spaceName }}><BellButton /></TopBar>
      <Title eyebrow={`${t.spaceName} · Track`} title={t.name}
        sub={<>{t.lead ? `Lead ${t.lead.name.split(' ')[0]} · ` : ''}{t.open} open of {t.total} · <span className={HEALTH[t.health][1]}>{HEALTH[t.health][0]}</span></>} />
      <BriefCard kind="track" id={t.id} label="This track" />
      <div className="gap16" />
      <Seg label="Track" value={tab} onChange={setTab} options={[['activities', 'Activities'], ['context', 'Context'], ['files', `Files · ${files.data?.length ?? 0}`]] as const} />
      <div className="gap16" />
      {tab === 'activities' && (acts.isLoading ? <SkelList rows={5} variant="conv" /> : (
        <div className="ph-grp" role="group" aria-label="Activities">
          {can(me.role, 'activity.write') && (
            <form className="ph-qadd" onSubmit={submit}>
              <span className="ph-ck plus" aria-hidden="true"><P.plus /></span>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Add a task" aria-label="Add a task" enterKeyHint="done" />
            </form>
          )}
          {(acts.data ?? []).map(a => <ActivityRow key={a.id} a={a} sub={a.owners[0]?.name.split(' ')[0] ?? 'No owner'} />)}
          {!acts.data?.length && <div className="ph-rowx"><span className="ph-rt"><div className="s">Nothing open in this track.</div></span></div>}
        </div>
      ))}
      {quick.error ? <ErrorNote error={quick.error} /> : null}
      {tab === 'context' && (
        <div className="ph-ctx" style={{ display: 'grid', gap: 12 }}>
          <ParentContext spaceId={t.spaceId} />
          <ContextPanel kind="track" id={t.id} spaceOf={t.spaceId} />
        </div>
      )}
      {tab === 'files' && <>
        <div className="ph-chips" role="tablist" aria-label="Which files">
          {([['all', 'All'], ['track', 'Track'], ['activities', 'From activities']] as const).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k} className={`ph-chip${filter === k ? ' on' : ''}`} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
        <div className="gap16" />
        {files.isLoading ? <SkelList rows={3} variant="conv" /> : <FileRows files={shown} where />}
        <div className="gap16" />
        <AddButton className="wide" onClick={() => setAdding(true)}>Add file</AddButton>
        {upload.isPending && <div className="ph-note" role="status">Uploading</div>}
        {upload.error ? <ErrorNote error={upload.error} /> : null}
        <div className="ph-note">Files attached to this track's activities show here too.</div>
      </>}
      <AnimatePresence>
        {adding && <AddFileSheet key="add" title={`Add to ${t.name} files`} ask={`Add to the ${t.name} files: `}
          onPick={f => upload.mutate(f, { onSuccess: () => toast(`Added ${f.name}`) })} onClose={() => setAdding(false)} />}
      </AnimatePresence>
    </div>
  );
}
